import { Injectable, inject } from "@angular/core";
import { Overlay, OverlayPositionBuilder } from "@angular/cdk/overlay";
import { TranslateService } from "@ngx-translate/core";
import { TourCdk } from "../tour-cdk";
import { TourConfig } from "../models/tour-config.model";
import { TOUR_BUTTON_CLASSES } from "../tour-styles";

@Injectable({
  providedIn: "root",
})
export class TourAfterLinkingStoreService {
  private readonly overlay = inject(Overlay);
  private readonly positionBuilder = inject(OverlayPositionBuilder);
  private readonly translate = inject(TranslateService);

  private currentTour: TourCdk | null = null;

  createTour(config: TourConfig): TourCdk {
    return new TourCdk(config, this.overlay, this.positionBuilder);
  }

  startTour(config: TourConfig): void {
    this.stop();
    this.currentTour = this.createTour(config);
    this.currentTour.start();
  }

  start(): void {
    if (this.currentTour?.isRunning()) {
      return;
    }

    this.startTour(this.buildAfterLinkingStoreTourConfig());
  }

  stop(): void {
    this.currentTour?.destroy();
    this.currentTour = null;
  }

  isRunning(): boolean {
    return this.currentTour?.isRunning() ?? false;
  }

  private buildAfterLinkingStoreTourConfig(): TourConfig {
    return {
      id: "tour-after-linking-store-cdk",
      name: this.translate.instant("tours.afterLinkingStore.name"),
      description: this.translate.instant(
        "tours.afterLinkingStore.description",
      ),
      showProgress: true,
      steps: [
        {
          id: "step-1-store-name",
          title: this.translate.instant("tours.afterLinkingStore.step1.title"),
          text: this.translate.instant("tours.afterLinkingStore.step1.text"),
          element: "[data-tour='afterLinkingStore-storeName']",
          position: "bottom",
          allowSkip: true,
          skipText: this.translate.instant("tours.afterLinkingStore.skip"),
          buttons: [
            {
              text: this.translate.instant(
                "tours.afterLinkingStore.step1.buttonNext",
              ),
              action: "next",
              classes: TOUR_BUTTON_CLASSES.primary,
            },
          ],
        },
        {
          id: "step-2-auto-remove",
          title: this.translate.instant("tours.afterLinkingStore.step2.title"),
          text: this.translate.instant("tours.afterLinkingStore.step2.text"),
          element: "[data-tour='afterLinkingStore-autoRemove']",
          position: "right",
          allowSkip: true,
          skipText: this.translate.instant("tours.afterLinkingStore.skip"),
          buttons: [
            {
              text: this.translate.instant(
                "tours.afterLinkingStore.step2.buttonNext",
              ),
              action: "next",
              classes: TOUR_BUTTON_CLASSES.primary,
            },
          ],
        },
        {
          id: "step-3-submit-store-step",
          title: this.translate.instant("tours.afterLinkingStore.step3.title"),
          text: this.translate.instant("tours.afterLinkingStore.step3.text"),
          element: "[data-tour='afterLinkingStore-submitStoreStep']",
          position: "bottom",
          allowSkip: true,
          skipText: this.translate.instant("tours.afterLinkingStore.skip"),
          buttons: [
            {
              text: this.translate.instant(
                "tours.afterLinkingStore.step3.buttonNext",
              ),
              action: "next",
              classes: TOUR_BUTTON_CLASSES.primary,
            },
          ],
        },
        {
          id: "step-4-next-multipart-button",
          title: this.translate.instant("tours.afterLinkingStore.step4.title"),
          text: this.translate.instant("tours.afterLinkingStore.step4.text"),
          element: "[data-tour='afterLinkingStore-nextMultipartStep']",
          position: "left",
          allowSkip: true,
          skipText: this.translate.instant("tours.afterLinkingStore.skip"),
          buttons: [
            {
              text: this.translate.instant(
                "tours.afterLinkingStore.step4.buttonDone",
              ),
              action: "cancel",
              classes: TOUR_BUTTON_CLASSES.primary,
            },
          ],
        },
      ],
    };
  }

  private findElement(selector: string): HTMLElement | null {
    if (selector.startsWith("by-text:")) {
      const searchText = selector.replace("by-text:", "");
      return this.findElementByText(searchText);
    }

    return document.querySelector(selector) as HTMLElement | null;
  }

  private async waitForElement(
    selector: string,
    timeoutMs = 3000,
    intervalMs = 100,
  ): Promise<HTMLElement | null> {
    const start = performance.now();

    while (performance.now() - start < timeoutMs) {
      const element = this.findElement(selector);
      if (
        element &&
        (element.offsetParent !== null || element.getClientRects().length > 0)
      ) {
        return element;
      }
      await this.delay(intervalMs);
    }

    return null;
  }

  private findElementByText(text: string): HTMLElement | null {
    const elements = Array.from(document.querySelectorAll("*"));
    return elements.find((el) =>
      el.textContent?.includes(text),
    ) as HTMLElement | null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
